import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RemoveButton from './RemoveButton';
import './ContactInfo.css';

const SortableContactField = ({ id, field, formData, handleChange, onRemove, isCustomField = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    backgroundColor: isDragging ? 'var(--color-background-hover, #f8f9fa)' : 'transparent',
    borderRadius: isDragging ? '8px' : '0',
    boxShadow: isDragging ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
    border: isDragging ? '2px solid var(--color-primary)' : '2px solid transparent',
    padding: isDragging ? '4px' : '0',
    margin: isDragging ? '2px 0' : '0',
  };

  const dragHandleStyle = {
    cursor: isDragging ? 'grabbing' : 'grab',
    padding: '8px',
    marginRight: '8px',
    color: isDragging ? 'var(--color-primary)' : 'var(--color-text-light)',
    fontSize: '16px',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    backgroundColor: isDragging ? 'rgba(255, 107, 53, 0.1)' : 'transparent',
  };

  const fieldConfig = {
    telephone: { label: 'Téléphone', placeholder: '+33 1 23 45 67 89', type: 'tel' },
    email: { label: 'Email *', placeholder: 'exemple@entreprise.com', type: 'email' },
    siteWeb: { label: 'Site web', placeholder: 'https://www.exemple.com', type: 'url' },
    adresse: { label: 'Adresse', placeholder: '123 Rue de la Paix, 75001 Paris', type: 'text' },
    agenda: { label: 'Agenda', placeholder: 'Prendre rendez-vous', type: 'agenda' },
    champsPersonnalise: { label: 'Champs personnalisé', placeholder: 'Votre information personnalisée', type: 'text' }
  };

  const config = isCustomField ? field : (typeof field === 'object' ? fieldConfig[field.key] : fieldConfig[field]) || { label: 'Champ', placeholder: '', type: 'text' };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="contact-field-item">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          {...listeners}
          style={dragHandleStyle}
          title="Glisser pour réorganiser"
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = 'rgba(255, 107, 53, 0.05)';
              e.target.style.color = 'var(--color-primary)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--color-text-light)';
            }
          }}
        >
          ⋮⋮
        </div>
        <div style={{ flex: 1 }}>
          <div className="contact-field-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label htmlFor={isCustomField ? field.id : (typeof field === 'object' ? field.key : field)}>{isCustomField ? field.label : config.label}</label>
            <RemoveButton onClick={() => onRemove(id)} />
          </div>
          {isCustomField ? (
            <div>
              <input
                type={config.type}
                id={field.id}
                name={`customField_${field.id}_value`}
                value={formData[`customField_${field.id}_value`] || ''}
                onChange={handleChange}
                placeholder="Votre information personnalisée"
              />
            </div>
          ) : config.type === 'agenda' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                type="text"
                id={`${field.key || field}_text`}
                name={`${field.key || field}_text`}
                value={formData[`${field.key || field}_text`] || ''}
                onChange={handleChange}
                placeholder="Texte affiché (ex: Prendre rendez-vous)"
                style={{ marginBottom: '5px' }}
              />
              <input
                type="url"
                id={`${field.key || field}_url`}
                name={`${field.key || field}_url`}
                value={formData[`${field.key || field}_url`] || ''}
                onChange={handleChange}
                placeholder="Lien vers votre agenda (ex: https://calendly.com/votre-nom)"
              />
            </div>
          ) : (
            <input
              type={config.type}
              id={typeof field === 'object' ? field.key : field}
              name={typeof field === 'object' ? field.key : field}
              value={formData[typeof field === 'object' ? field.key : field] || ''}
              onChange={handleChange}
              placeholder={config.placeholder}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ContactInfo = ({ formData, updateFormData }) => {
  const [showAvailableFields, setShowAvailableFields] = useState(false);
  const [customFields, setCustomFields] = useState(formData.customFields || []);
  const [contactOrder, setContactOrder] = useState(formData.contactOrder || []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Configuration des champs disponibles
  const availableFields = [
    { key: 'telephone', label: 'Téléphone', placeholder: '+33 1 23 45 67 89', type: 'tel' },
    { key: 'email', label: 'Email', placeholder: 'exemple@entreprise.com', type: 'email' },
    { key: 'siteWeb', label: 'Site web', placeholder: 'https://www.exemple.com', type: 'url' },
    { key: 'adresse', label: 'Adresse', placeholder: '123 Rue de la Paix, 75001 Paris', type: 'text' },
    { key: 'agenda', label: 'Agenda', placeholder: 'Prendre rendez-vous', type: 'agenda' },
    { key: 'champsPersonnalise', label: 'Champs personnalisé', placeholder: 'Votre information personnalisée', type: 'text' }
  ];



  // Synchroniser customFields avec formData
  React.useEffect(() => {
    if (formData.customFields && JSON.stringify(formData.customFields) !== JSON.stringify(customFields)) {
      setCustomFields(formData.customFields);
    }
  }, [formData.customFields, customFields]);

  // Synchroniser contactOrder avec formData.contactOrder
  React.useEffect(() => {
    if (formData.contactOrder && JSON.stringify(formData.contactOrder) !== JSON.stringify(contactOrder)) {
      setContactOrder(formData.contactOrder);
    }
  }, [formData.contactOrder, contactOrder]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData(name, value);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = contactOrder.findIndex(item => item.id === active.id);
      const newIndex = contactOrder.findIndex(item => item.id === over.id);
      const newOrder = arrayMove(contactOrder, oldIndex, newIndex);
      
      setContactOrder(newOrder);
      updateFormData('contactOrder', newOrder);
    }
  };

  // Ajouter un champ prédéfini
  const addPredefinedField = (fieldKey) => {
    // Si c'est un champ personnalisé, utiliser la fonction dédiée
    if (fieldKey === 'champsPersonnalise') {
      addCustomField();
      return;
    }
    
    // Ajouter le champ au formData
    updateFormData(fieldKey, '');
    
    // Ajouter immédiatement le champ à contactOrder
    const fieldData = availableFields.find(f => f.key === fieldKey);
    if (fieldData) {
      const newOrderItem = {
        id: `predefined_${fieldKey}`,
        type: 'predefined',
        key: fieldKey,
        data: fieldData,
        order: contactOrder.length
      };
      const updatedOrder = [...contactOrder, newOrderItem];
      setContactOrder(updatedOrder);
      updateFormData('contactOrder', updatedOrder);
    }
    
    setShowAvailableFields(false);
  };

  // Ajouter un champ personnalisé (un seul autorisé)
  const addCustomField = () => {
    // Vérifier qu'il n'y a pas déjà un champ personnalisé actif
    if (contactOrder.some(item => item.type === 'custom')) {
      return; // Ne pas ajouter si un champ personnalisé existe déjà
    }
    
    const newField = {
      id: Date.now(),
      label: 'Champ personnalisé',
      type: 'text'
    };
    const updatedFields = [...customFields, newField];
    setCustomFields(updatedFields);
    updateFormData('customFields', updatedFields);
    
    // Ajouter immédiatement le champ à contactOrder
    const newOrderItem = {
      id: `custom_${newField.id}`,
      type: 'custom',
      key: newField.id,
      data: newField,
      order: contactOrder.length
    };
    const updatedOrder = [...contactOrder, newOrderItem];
    setContactOrder(updatedOrder);
    updateFormData('contactOrder', updatedOrder);
    
    setShowAvailableFields(false);
  };

  // Supprimer un champ
  const removeField = (fieldId) => {
    const field = contactOrder.find(item => item.id === fieldId);
    
    if (field) {
      if (field.type === 'predefined') {
        // Supprimer le champ prédéfini du formData
        updateFormData(field.key, undefined);
      } else if (field.type === 'custom') {
        // Supprimer le champ personnalisé
        const updatedFields = customFields.filter(f => f.id !== field.key);
        setCustomFields(updatedFields);
        updateFormData('customFields', updatedFields);
        
        // Supprimer les données du champ personnalisé
        updateFormData(`customField_${field.key}_label`, undefined);
        updateFormData(`customField_${field.key}_value`, undefined);
      }
      
      // Mettre à jour l'ordre en supprimant le champ
      const updatedOrder = contactOrder.filter(item => item.id !== fieldId);
      setContactOrder(updatedOrder);
      updateFormData('contactOrder', updatedOrder);
    }
  };

  // Obtenir les champs disponibles (non encore ajoutés)
  const getAvailableFields = () => {
    return availableFields.filter(field => {
      // Exclure les champs prédéfinis déjà ajoutés
      if (contactOrder.some(item => item.type === 'predefined' && item.key === field.key)) {
        return false;
      }
      
      // Exclure le champ personnalisé s'il y en a déjà un actif dans contactOrder
      if (field.key === 'champsPersonnalise' && contactOrder.some(item => item.type === 'custom')) {
        return false;
      }
      
      return true;
    });
  };

  return (
    <div className="form-section-content">
      <h3 className="section-title">Informations de contact</h3>
      
      {/* Section Champs ajoutés */}
      <div style={{ marginBottom: '30px' }}>
        {contactOrder.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={contactOrder.filter(item => item.id).map(item => item.id)} strategy={verticalListSortingStrategy}>
              {contactOrder.filter(item => item.id).map((item) => {
                const isCustom = item.type === 'custom';
                const fieldData = isCustom ? item.data : availableFields.find(f => f.key === item.key);
                return (
                  <SortableContactField
                    key={item.id}
                    id={item.id}
                    field={fieldData || item.key}
                    formData={formData}
                    handleChange={handleChange}
                    onRemove={removeField}
                    isCustomField={isCustom}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          <p style={{ color: 'var(--color-text-light)', fontStyle: 'italic' }}>
            Aucun champ ajouté. Utilisez le bouton ci-dessous pour en ajouter.
          </p>
        )}

        {/* Bouton d'ajout */}
        <button
          type="button"
          onClick={() => setShowAvailableFields(!showAvailableFields)}
          style={{
            background: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginTop: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>+</span>
          Ajout d'informations de contact
        </button>

        {/* Liste des champs disponibles */}
        {showAvailableFields && (
          <div style={{
            marginTop: '15px',
            padding: '15px',
            backgroundColor: 'var(--color-background-light, #f8f9fa)',
            borderRadius: '8px',
            border: '1px solid var(--color-border, #e9ecef)'
          }}>
            <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>
              Champs disponibles :
            </h5>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {/* Champs prédéfinis disponibles */}
              {getAvailableFields().map((field) => (
                <button
                  key={field.key}
                  type="button"
                  onClick={() => addPredefinedField(field.key)}
                  style={{
                    background: 'white',
                    border: '1px solid var(--color-border, #ddd)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--color-primary)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = 'inherit';
                  }}
                >
                  {field.label}
                </button>
              ))}
              

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactInfo;